import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { HUB_SEO, TOOLS_BASE, toolPath } from '@/lib/toolRoutes';
import Logo from '@/components/Logo';
import {
  ChartBar, Target, Globe, CurrencyCircleDollar, ArrowRight,
  Sparkle,
} from '@phosphor-icons/react';

const TOOL_DEFS = [
  { key: 'geo',    iconKey: 'globe',  badgeKey: 'tools.cards.geo.badge',   titleKey: 'tools.cards.geo.title',   descKey: 'tools.cards.geo.desc',   ctaKey: 'tools.cards.geo.cta',   color: 'from-teal-500/20 to-emerald-500/20', testSlug: 'geo-checkup' },
  { key: 'rakip',  iconKey: 'target', badgeKey: 'tools.cards.rakip.badge', titleKey: 'tools.cards.rakip.title', descKey: 'tools.cards.rakip.desc', ctaKey: 'tools.cards.rakip.cta', color: 'from-blue-500/20 to-indigo-500/20', testSlug: 'rakip-radar' },
  { key: 'rapor',  iconKey: 'chart',  badgeKey: 'tools.cards.rapor.badge', titleKey: 'tools.cards.rapor.title', descKey: 'tools.cards.rapor.desc', ctaKey: 'tools.cards.rapor.cta', color: 'from-purple-500/20 to-pink-500/20', testSlug: 'rapor-analitik' },
  { key: 'fiyat',  iconKey: 'money',  badgeKey: 'tools.cards.fiyat.badge', titleKey: 'tools.cards.fiyat.title', descKey: 'tools.cards.fiyat.desc', ctaKey: 'tools.cards.fiyat.cta', color: 'from-amber-500/20 to-orange-500/20', testSlug: 'fiyatlama' },
];

const ICONS = {
  globe: <Globe size={28} weight="bold" />,
  target: <Target size={28} weight="bold" />,
  chart: <ChartBar size={28} weight="bold" />,
  money: <CurrencyCircleDollar size={28} weight="bold" />,
};

export default function ToolsHubPage() {
  const { isAuthenticated } = useAuth();
  const { language, t } = useLanguage();
  const seo = HUB_SEO[language] || HUB_SEO.tr;
  useDocumentMeta({
    title: seo.title,
    description: seo.description,
    canonical: TOOLS_BASE[language],
    alternates: { tr: TOOLS_BASE.tr, en: TOOLS_BASE.en, 'x-default': TOOLS_BASE.tr },
    ogKind: 'tool',
  });

  return (
    <div className="min-h-screen bg-[#0F1B36] text-white" data-testid="tools-hub-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0F1B36]/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Logo variant="dark" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-sm text-white/70 hover:text-white">{t('footer.blog')}</Link>
            <Link to="/pricing" className="text-sm text-white/70 hover:text-white">{t('footer.pricing')}</Link>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Link to="/dashboard" className="px-4 py-2 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold text-sm" data-testid="dashboard-cta">{t('nav.dashboard')}</Link>
            ) : (
              <Link to="/login" className="px-4 py-2 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold text-sm" data-testid="login-cta">{t('nav.login')}</Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3CD9B8]/10 border border-[#3CD9B8]/30 text-[#3CD9B8] text-xs font-medium mb-6">
          <Sparkle size={14} weight="fill" /> {t('tools.hub.badge')}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
          {t('tools.hub.title.line1')}
          <br />
          <span className="text-[#3CD9B8]">{t('tools.hub.title.highlight')}</span> {t('tools.hub.title.line2')}
        </h1>
        <p className="text-lg text-white/70 max-w-2xl mx-auto">
          {t('tools.hub.subtitle')} <Link to="/register" className="text-[#3CD9B8] underline">{t('tools.hub.subtitle.cta')}</Link>.
        </p>
      </section>

      {/* Tools grid */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          {TOOL_DEFS.map((tool) => (
            <Link
              key={tool.key}
              to={toolPath(tool.key, language)}
              data-testid={`tool-card-${tool.testSlug}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 hover:bg-white/[0.08] hover:border-[#3CD9B8]/40 transition p-8"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-100 transition`} />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#3CD9B8]/10 flex items-center justify-center text-[#3CD9B8]">
                    {ICONS[tool.iconKey]}
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">{t(tool.badgeKey)}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{t(tool.titleKey)}</h3>
                <p className="text-sm text-white/70 mb-6 leading-relaxed">{t(tool.descKey)}</p>
                <div className="flex items-center gap-2 text-[#3CD9B8] font-semibold text-sm">
                  {t(tool.ctaKey)} <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Funnel CTA */}
        <div className="mt-16 rounded-2xl border border-[#3CD9B8]/30 bg-gradient-to-br from-[#3CD9B8]/10 to-transparent p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">{t('tools.hub.upsell.title')}</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-6">
            {t('tools.hub.upsell.body')}
          </p>
          <Link
            to="/register"
            data-testid="upsell-cta"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold hover:opacity-90 transition"
          >
            {t('tools.hub.upsell.cta')} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
